import { uploadPrivateFile, uploadPublicFile } from "@/shared/api/uploadFile";
import { NewsItem, TalentExperienceData } from "../types";

/*
 * Shared types and utility helpers used across the Create Card flow.
 *
 * This file holds small cross-cutting building blocks that are reused by the container,
 * hydration logic, preview transformation, and payload transformation. The goal is to keep
 * those modules consistent when converting between UI names, local state keys, preview types,
 * and upload behavior.
 */
export interface CreateCardPage {
  id: string;
  name: string;
}

export interface CreateCardPageItems {
  talentExperience?: string;
  mods?: string;
  stickers?: string;
}

export interface CreateCardGlobalItems {
  template?: string;
  premium?: string;
  limited?: string;
}

export type CreateCardTranslate = (key: string, options?: Record<string, unknown>) => string;

// Card experiences that are treated as mutually exclusive across pages.
export const exclusiveCardKeys: string[] = [
  "instant_card",
  "flash_card",
  "stage_card",
  "busking_card",
  "stai_card",
  "heart_card",
  "diamond_card",
  "clover_card",
  "spade_card",
  "dear_card",
  "ait_card",
  "song4u_card",
  "single_card",
  "k_indie_card",
  "fan_club_card",
  "bm_card",
];

// Maps local experience keys to the page `type` strings expected by preview/payload consumers.
export const keyToTypeMap: Record<string, string> = {
  cover_image: "Cover_Image",
  instant_card: "Instant_Card",
  flash_card: "Flash_Card",
  stage_card: "Stage_Card",
  busking_card: "Busking_Card",
  stai_card: "Stai_Card",
  heart_card: "Taas_Card",
  spade_card: "Taas_Card",
  diamond_card: "Diamond_Card",
  clover_card: "Clover_Card",
  image_grid: "Image",
  image: "Image",
  text: "Text",
  about: "About",
  bonus: "Bonus",
  instrument: "Instrument",
  songs: "Songs",
  audio: "Audio",
  video: "Video",
  gift_card: "Gift_Card",
  snap_flyer: "Snap_Flyer",
  dear_card: "Dear_Card",
  ait_card: "Ait_Card",
  song4u_card: "Song4U_Card",
  bm_card: "BM_Card",
  single_card: "Single_Card",
  k_indie_card: "K_Indie_Card",
  song4you_songs: "Song",
  fan_club_card: "Fan_Club_Card",
  welcome: "Welcome",
  post_card: "Post_Card",
  youtube_experience: "Youtube",
  news: "News",
};

// Maps a translated/local experience key to the corresponding property inside `TalentExperienceData`.
export const getDataKeyFromKey = (key: string): keyof TalentExperienceData | null => {
  const keyToDataKey: Record<string, keyof TalentExperienceData> = {
    cover_image: "coverImage",
    image: "image",
    audio: "audio",
    video: "video",
    image_grid: "imageGrid",
    text: "text",
    about: "about",
    bonus: "bonus",
    instrument: "instrument",
    songs: "songs",
    instant_card: "instantCard",
    flash_card: "flashCard",
    stage_card: "stageCard",
    busking_card: "buskingCard",
    stai_card: "staiCard",
    heart_card: "taasCard",
    spade_card: "taasCard",
    diamond_card: "taasCard",
    clover_card: "taasCard",
    gift_card: "giftCard",
    snap_flyer: "snapFlyer",
    dear_card: "dearCard",
    ait_card: "aitCard",
    bm_card: "bmCard",
    single_card: "singleCard",
    k_indie_card: "kIndieCard",
    song4u_card: "song4uCard",
    song4you_songs: "song4uSongs",
    fan_club_card: "fanClubCard",
    post_card: "postCard",
    youtube_experience: "youtube",
    news: "news",
  };

  return keyToDataKey[key] || null;
};

// Normalizes legacy or inconsistent news shapes into a single array format used by the editor helpers.
export const normalizeNewsContent = (content: any): NewsItem[] => {
  if (Array.isArray(content)) {
    return content.map((item) => ({
      title: item?.title || "",
      description: item?.description || "",
      url: item?.url || "",
    }));
  }

  if (Array.isArray(content?.news)) {
    return content.news.map((item: any) => ({
      title: item?.title || "",
      description: item?.description || "",
      url: item?.url || "",
    }));
  }

  if (content?.title || content?.description || content?.url) {
    return [
      {
        title: content.title || "",
        description: content.description || "",
        url: content.url || "",
      },
    ];
  }

  return [];
};

// Identifies whether the provided experience key belongs to one of the TAAS card suit variants.
export const isTaasCardKey = (key: string | null): boolean =>
  key === "heart_card" || key === "spade_card" || key === "diamond_card" || key === "clover_card";

// Converts a TAAS card experience key into the suit name needed by default-data helpers.
export const getTaasCardSuit = (
  key: string | null
): "Heart" | "Spade" | "Diamond" | "Clover" | undefined => {
  if (key === "heart_card") return "Heart";
  if (key === "spade_card") return "Spade";
  if (key === "diamond_card") return "Diamond";
  if (key === "clover_card") return "Clover";
  return undefined;
};

/*
 * Upload helper for values that may already be URLs, local template assets, or base64 strings.
 *
 * Behavior:
 * - uploads built-in template images when they are still local asset imports
 * - uploads base64 data URLs after converting them to `File`
 * - leaves already-remote URLs unchanged
 */
export const uploadIfNeeded = async (
  url: string | undefined,
  module: string = "Card"
): Promise<string> => {
  if (!url) return "";

  // If it's a base64 data URL, convert to File and upload
  if (url.startsWith("data:")) {
    const [mimePart, base64] = url.split(",");
    const mime = mimePart.match(/:(.*?);/)?.[1] || "image/png";
    const bytes = Uint8Array.from(atob(base64), (c) => c.charCodeAt(0));
    const file = new File([bytes], `file.${mime.split("/")[1] || "png"}`, {
      type: mime,
    });

    return await uploadPrivateFile(file, module);
  }

  // Otherwise, assume it's an already uploaded/remote URL and return as-is
  return url;
};

export const uploadPubliclyIfNeeded = async (
  url: string | undefined,
  module: string = "Card"
): Promise<string> => {
  if (!url) return "";

  // If it's a base64 data URL, convert to File and upload
  if (url.startsWith("data:")) {
    const [mimePart, base64] = url.split(",");
    const mime = mimePart.match(/:(.*?);/)?.[1] || "image/png";
    const bytes = Uint8Array.from(atob(base64), (c) => c.charCodeAt(0));
    const file = new File([bytes], `file.${mime.split("/")[1] || "png"}`, {
      type: mime,
    });

    return await uploadPublicFile(file, module);
  }

  // Otherwise, assume it's an already uploaded/remote URL and return as-is
  return url;
};

// Accepts either a `File` or string-like media value and returns a final uploaded URL.
export const uploadFileOrReturnUrl = async (
  fileOrUrl: File | string | null | undefined,
  module: string = "Card"
): Promise<string> => {
  if (!fileOrUrl) return "";
  if (typeof fileOrUrl === "string") {
    // If it's a string, use uploadIfNeeded to handle base64 or regular URLs
    return await uploadIfNeeded(fileOrUrl, module);
  }
  if (fileOrUrl instanceof File) {
    // If it's a File object, upload it
    return await uploadPrivateFile(fileOrUrl, module);
  }
  return "";
};

export const uploadFileOrReturnPublicUrl = async (
  fileOrUrl: File | string | null | undefined,
  module: string = "Card"
): Promise<string> => {
  if (!fileOrUrl) return "";
  if (typeof fileOrUrl === "string") {
    // If it's a string, use uploadIfNeeded to handle base64 or regular URLs
    return await uploadPubliclyIfNeeded(fileOrUrl, module);
  }
  if (fileOrUrl instanceof File) {
    // If it's a File object, upload it
    return await uploadPublicFile(fileOrUrl, module);
  }
  return "";
};
